import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {loadImageAsync} from '@Utils/image.util';
import {degreesToRadians} from '@Utils/math.util';
import {fabricObjectToDataUrl} from '@Utils/fabric.util';
import {FabricImage} from '@postermywall/fabricjs-2';

export interface ItemCropObject {
  cropped: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  imageWidth: number;
  imageHeight: number;
  /**
   * Degrees the picture is turned about the window's centre before the window is cut out - a
   * straightened crop. 0 is a plain rectangle of the source. Clockwise positive, like fabric's angle.
   */
  rotation: number;
}

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A crop window in the media's own pixels, without the flags that say whether and how it is applied. */
export type ItemCropRect = Pick<ItemCropObject, 'x' | 'y' | 'width' | 'height' | 'imageWidth' | 'imageHeight'>;

export interface CenteredCropForAspectRatioParams {
  mediaWidth: number;
  mediaHeight: number;
  aspectRatio: number;
}

export class ItemCrop extends CommonMethods {
  public cropped = false;
  public x = 0;
  public y = 0;
  public width = 0;
  public height = 0;
  public imageWidth = 0;
  public imageHeight = 0;
  public rotation = 0;

  public toObject(): ItemCropObject {
    return {
      cropped: this.cropped,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      imageWidth: this.imageWidth,
      imageHeight: this.imageHeight,
      rotation: this.rotation,
    };
  }

  public async applyCropToImage(imageElement: HTMLImageElement): Promise<HTMLImageElement> {
    if (!this.cropped) {
      return imageElement;
    }
    const cropData = this.getScaledCropData(imageElement);
    if (this.rotation === 0) {
      const fabricItem = new FabricImage(imageElement, {});
      return loadImageAsync(
        fabricObjectToDataUrl(fabricItem, {
          left: cropData.x,
          top: cropData.y,
          width: cropData.width,
          height: cropData.height,
        })
      );
    }
    return loadImageAsync(this.#drawStraightenedCrop(imageElement, cropData));
  }

  /**
   * The picture turned about the window's centre, then the window cut out. The same drawing serves
   * the editor and poster generation, which renders this canvas too.
   */
  #drawStraightenedCrop(imageElement: HTMLImageElement, cropData: CropData): string {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, cropData.width);
    canvas.height = Math.max(1, cropData.height);
    const context = canvas.getContext('2d');
    if (!context) {
      return imageElement.src;
    }
    context.imageSmoothingQuality = 'high';
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate(degreesToRadians(this.rotation));
    context.drawImage(imageElement, -(cropData.x + cropData.width / 2), -(cropData.y + cropData.height / 2));
    return canvas.toDataURL();
  }

  public isCropped(): boolean {
    return this.cropped;
  }

  public getScaledCropData(img: HTMLImageElement | HTMLVideoElement): CropData {
    const scaleXDelta = img.width / this.imageWidth;
    const scaleYDelta = img.height / this.imageHeight;

    return {
      x: Math.round(this.x * scaleXDelta),
      y: Math.round(this.y * scaleYDelta),
      width: Math.round(this.width * scaleXDelta),
      height: Math.round(this.height * scaleYDelta),
    };
  }
}
